/**
 * Display formatting for values the API returns as strings.
 *
 * Dates arrive as 'YYYY-MM-DD' and times as 'HH:MM' (UTC, per schema.sql).
 * They are parsed as plain parts rather than fed to `new Date(string)`, which
 * would apply the viewer's timezone to a date that has none and can render a
 * Friday night as Thursday for anyone west of UTC.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** '1 Sep 2026' — no weekday. Date ranges and season lines want this; a
 *  fixture night wants formatDate, where the weekday is the useful part. */
export function formatDateShort(value) {
  if (!value) return null;
  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) return String(value);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export function formatDate(value) {
  const short = formatDateShort(value);
  if (!short) return null;

  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) return short;

  // Date.UTC purely to get the weekday — the parts above are what is rendered.
  return `${DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${short}`;
}

export function formatTime(value) {
  if (!value) return null;
  const [h, min] = String(value).split(':');
  if (h === undefined || min === undefined) return String(value);
  return `${h}:${min}`;
}

/** 'Fri 12 Sep 2025, 19:30' — either half omitted if absent. */
export function formatDateTime(date, time) {
  return [formatDate(date), formatTime(time)].filter(Boolean).join(', ') || null;
}

/**
 * '2026-09-07 00:37:23' -> '7 Sep 2026'.
 *
 * Columns defaulting to CURRENT_TIMESTAMP store a datetime, not a date, and
 * formatDateShort splits on '-' — handed the full stamp it parses the day as
 * '07 00:37:23', gets NaN, and returns the raw string. Trimming to the date
 * part is the whole job.
 */
export const formatStampDate = (value) =>
  formatDateShort(String(value ?? '').slice(0, 10));

export const formatAvg = (n) => (typeof n === 'number' ? n.toFixed(2) : '—');
